import React, { ChangeEvent, useEffect, useRef } from 'react';
import { Drawer, Button, Input, InputNumber, Form, Image, Select } from 'antd';
import { CloseOutlined, FileImageOutlined, PlusOutlined } from '@ant-design/icons';
import {
	uploadPhotos,
	uploadCover,
	removePhoto,
	changeComplex,
	closeModal,
	getComplexById,
	removeCover,
	addMultiSelect,
	removeMultiSelect,
	EditComplexModalSelector,
	changeArrayItem,
	addArrayItem,
	removeArrayItem,
} from './EditComplexModalSlice';
import { defaultValues } from 'consts/default';
import * as S from './EditComplexModal.styled';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import {
	IAmenity,
	IComplexPhoto,
	IConsultation,
	IRegion,
	IFavourite,
	ILocation,
	IOrganization,
	IPart,
} from 'interfaces/main';
import { exchangeComplex } from 'features/ComplexesList/ComplexesListSlice';
import { unwrapResult } from '@reduxjs/toolkit';
import { OrganizationsListItemsSelector } from 'features/OrganizationsList/OrganizationsListSlice';
import { JSX } from 'react/jsx-runtime';
import dayjs from 'dayjs';
import { getQuart } from 'utils/help';
import { LibrariesSelector } from 'features/Libraries/LibrariesSlice';
import Spinner from 'components/Spinner';

const layout = {
	labelCol: { span: 6 },
	wrapperCol: { span: 18 },
};

const { TextArea } = Input;
const { Option } = Select;

const EditComplexModal = (): JSX.Element => {
	const dispatch = useAppDispatch();

	const [form] = Form.useForm();

	const photosInputRef = useRef<HTMLInputElement | null>(null);
	const coverInputRef = useRef<HTMLInputElement | null>(null);

	// Данные организации
	const { data, isLoading, isPhotosLoading, isVisible, isCoverLoading } =
		useAppSelector(EditComplexModalSelector);

	// Доступные организации
	const allOrganizations = useAppSelector(OrganizationsListItemsSelector);

	// Словари
	const {
		amenities: allAmenities,
		regions: allRegions,
		locations: allLocations,
	} = useAppSelector(LibrariesSelector);

	const {
		id,
		title,
		description_de,
		description_ru,
		description_en,
		longitude,
		latitude,
		year,
		quart,
		rating,
		complexCover,
		complexPhotos,
		organizationId,
		regionId,
		amenities,
		locations,
		favourites,
		consultations,
		parts,
	} = data;

	// Получение данных организации
	useEffect(() => {
		if (id)
			dispatch(getComplexById({ id }))
				.then(unwrapResult)
				.then((fetchedComplex) => {
					const l = {
						...fetchedComplex,
						locations: fetchedComplex.locations.map((item: ILocation) => ({
							key: item.id,
							value: item.name,
						})),
						amenities: fetchedComplex.amenities.map((item: IAmenity) => ({
							key: item.id,
							value: item.name_en,
						})),
						favourites: fetchedComplex.favourites.map((item: IFavourite) => ({
							key: item.id,
							value: `userId: ${item.userId}`,
						})),
						consultations: fetchedComplex.consultations.map((item: IConsultation) => ({
							key: item.id,
							value: `userId: ${item.userId}`,
						})),
					};

					const fields = Object.keys(l).map((fieldName: string) => ({
						name: fieldName,
						value: l[fieldName],
					}));

					form.setFields(fields);
				});
	}, [dispatch, form, id]);

	// Закрытие окна
	const onClose = () => {
		form.submit();
	};

	// Загрузка новых фотографий
	const onUploadPhotos = (event: ChangeEvent<HTMLInputElement>) => {
		let newFileList = [...event.target.files];

		if (!!newFileList.length) {
			dispatch(uploadPhotos({ files: newFileList, complexId: id }));
		}
	};

	// Загрузка обложка
	const onUploadCover = (event: ChangeEvent<HTMLInputElement>) => {
		let newFile = event.target.files[0];

		if (!!newFile) {
			dispatch(uploadCover({ file: newFile, complexId: id }));
		}
	};

	const onUploadPhotosClick = () => {
		photosInputRef.current?.click();
	};

	const onUploadCoverClick = () => {
		coverInputRef.current?.click();
	};

	const onRemovePhoto = (id: number) => {
		dispatch(removePhoto({ id }));
	};

	const onSubmit = () => {
		dispatch(exchangeComplex(data));
		dispatch(closeModal());
	};

	const onRemoveCover = () => {
		dispatch(removeCover(complexCover.id));
	};

	// Ошибки в форме при сабмите
	const onError = (errors) => console.log(errors);

	// Изменение основных полей организации
	const onChangeComplex = (key: string, value: string | number | boolean) =>
		dispatch(changeComplex({ key, complexId: id, value }));

	// Изменение текстового поля
	const onChangeTextField = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
		onChangeComplex(event.target.name, event.target.value);

	// Изменение числового поля
	const onChangeNumberField = (name: string, value: number) => onChangeComplex(name, value);

	// Добавление в мультиселектах
	const onAddMultiSelect = (
		objectId: number,
		key: 'location' | 'amenity' | 'favourite' | 'consultation',
	) => {
		dispatch(addMultiSelect({ objectId, complexId: id, key }));
	};

	// Добавление в мультиселектах
	const onRemoveMultiSelect = (
		objectId: number,
		key: 'location' | 'amenity' | 'favourite' | 'consultation',
	) => dispatch(removeMultiSelect({ objectId, complexId: id, key }));

	// Добавление элемента массива
	const onAddArrayItem = (arrayName: 'part') => {
		dispatch(
			addArrayItem({
				arrayName,
				item: {
					...defaultValues[arrayName],
					complexId: id,
				},
			}),
		);
	};

	// Изменение элемента массива
	const onChangeArrayItem = (
		arrayName: 'part',
		itemId: number,
		value: string | number | boolean,
		key: string,
	) => dispatch(changeArrayItem({ arrayName, key, itemId, value }));

	// Удаление элемента массива
	const onRemoveArrayItem = (arrayName: 'part', itemId: number) =>
		dispatch(removeArrayItem({ arrayName, itemId }));

	return (
		<Drawer title="Edit complex" placement="right" width={700} onClose={onClose} open={isVisible}>
			{(isCoverLoading || isCoverLoading || isLoading) && <Spinner />}
			<Form
				{...layout}
				name="editComplexForm"
				id="editComplexForm"
				form={form}
				initialValues={{
					title,
					description_de,
					description_en,
					description_ru,
					longitude,
					latitude,
					year,
					quart,
					rating,
					amenities: amenities.map((item: IAmenity) => ({
						key: item.id,
						value: item.name_en,
					})),
					locations: locations.map((item: ILocation) => ({
						key: item.id,
						value: item.name,
					})),
					organizationId,
					regionId,
					parts,
					favourites: favourites.map((item: IFavourite) => ({
						key: item.id,
						value: `userId: ${item.userId}`,
					})),
					consultations: consultations.map((item: IConsultation) => ({
						key: item.id,
						value: `userId: ${item.userId}`,
					})),
				}}
				onFinish={onSubmit}
				onError={onError}>
				<Form.Item
					name="title"
					label="Title"
					rules={[{ required: true, message: "Please input complex's title" }]}>
					<Input type="text" placeholder="zDegree DIP" name="title" onChange={onChangeTextField} />
				</Form.Item>
				<Form.Item name="description_en" label="Description (EN)">
					<TextArea
						style={{ width: '100%' }}
						rows={2}
						placeholder="129, 318 Roud, Al Quoz Fourth - Dubai"
						maxLength={1000}
						name="description_en"
						onChange={onChangeTextField}
					/>
				</Form.Item>
				<Form.Item name="description_ru" label="Description (RU)">
					<TextArea
						style={{ width: '100%' }}
						rows={2}
						placeholder="129, 318 Roud, Al Quoz Fourth - Dubai"
						maxLength={1000}
						name="description_ru"
						onChange={onChangeTextField}
					/>
				</Form.Item>
				<Form.Item name="description_de" label="Description (DE)">
					<TextArea
						style={{ width: '100%' }}
						rows={2}
						placeholder="129, 318 Roud, Al Quoz Fourth - Dubai"
						maxLength={1000}
						name="description_de"
						onChange={onChangeTextField}
					/>
				</Form.Item>
				<Form.Item
					name="longitude"
					label="Longitude"
					rules={[{ required: true, message: 'Please input longitude' }]}>
					<InputNumber
						type="text"
						name="longitude"
						placeholder="59.83"
						min={52.0}
						max={59.83}
						style={{ width: '100%' }}
						onChange={(value: number) => onChangeNumberField('longitude', value)}
					/>
				</Form.Item>
				<Form.Item
					name="latitude"
					label="Latitude"
					rules={[{ required: true, message: 'Please input latitude' }]}>
					<InputNumber
						type="text"
						name="latitude"
						placeholder="26.50"
						min={16.65}
						max={26.5}
						style={{ width: '100%' }}
						onChange={(value: number) => onChangeNumberField('latitude', value)}
					/>
				</Form.Item>
				<Form.Item
					name="year"
					label="Completion year"
					rules={[{ required: true, message: 'Please input complition year' }]}>
					<InputNumber
						type="text"
						name="year"
						placeholder={dayjs().year().toString()}
						style={{ width: '100%' }}
						onChange={(value: number) => onChangeNumberField('year', value)}
					/>
				</Form.Item>
				<Form.Item
					name="quart"
					label="Completion quart"
					rules={[{ required: true, message: 'Please input complition quart' }]}>
					<InputNumber
						type="text"
						name="quart"
						placeholder={getQuart(dayjs().month()).toString()}
						style={{ width: '100%' }}
						onChange={(value: number) => onChangeNumberField('quart', value)}
					/>
				</Form.Item>
				<Form.Item name="rating" label="Rating">
					<InputNumber
						type="text"
						name="rating"
						placeholder="0"
						style={{ width: '100%' }}
						onChange={(value: number) => onChangeNumberField('rating', value)}
					/>
				</Form.Item>
				<Form.Item name="organizationId" label="Organization">
					<Select
						style={{ width: '100%' }}
						placeholder="Please select"
						onChange={(value: number) => onChangeNumberField('organizationId', value)}>
						{allOrganizations.map((item: IOrganization) => (
							<Option key={item.id} value={item.id}>
								{item.title}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item
					name="regionId"
					label="Region"
					rules={[{ required: true, message: 'Please select Region' }]}>
					<Select
						style={{ width: '100%' }}
						placeholder="Please select Region"
						onChange={(value: number) => onChangeNumberField('regionId', value)}>
						{allRegions.map((item: IRegion) => (
							<Option key={item.id} value={item.id}>
								{item.name_en}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item name="locations" label="Locations">
					<Select
						mode="multiple"
						style={{ width: '100%' }}
						placeholder="Please select"
						options={allLocations.map((item: ILocation) => ({
							key: item.id,
							value: item.name,
						}))}
						onSelect={(_: number, option) => onAddMultiSelect(option.key, 'location')}
						onDeselect={(_: number, option) => onRemoveMultiSelect(option.key, 'location')}
					/>
				</Form.Item>
				<Form.Item name="amenities" label="Amenities">
					<Select
						mode="multiple"
						style={{ width: '100%' }}
						placeholder="Please select"
						onSelect={(_: number, option) => onAddMultiSelect(option.key, 'amenity')}
						onDeselect={(_: number, option) => onRemoveMultiSelect(option.key, 'amenity')}
						options={allAmenities.map((item: IAmenity) => ({
							key: item.id,
							value: item.name_en,
						}))}
					/>
				</Form.Item>
				<Form.Item label="Payment parts">
					{[...parts]
						.sort((a: IPart, b: IPart) => a.order - b.order)
						.map((part: IPart) => (
							<S.Part key={part.id}>
								<Input.Group>
									<Input
										style={{ marginRight: 6, flexGrow: 1, borderRadius: 6 }}
										placeholder="On booking"
										value={part.title_en}
										onChange={(event) =>
											onChangeArrayItem('part', part.id, event.target.value, 'title_en')
										}
									/>
								</Input.Group>
								<Input.Group>
									<Input
										style={{ marginRight: 6, flexGrow: 1, borderRadius: 6 }}
										placeholder="При бронировании"
										value={part.title_ru}
										onChange={(event) =>
											onChangeArrayItem('part', part.id, event.target.value, 'title_ru')
										}
									/>
								</Input.Group>
								<Input.Group>
									<Input
										style={{ marginRight: 6, flexGrow: 1, borderRadius: 6 }}
										placeholder="Bei der Buchung"
										value={part.title_de}
										onChange={(event) =>
											onChangeArrayItem('part', part.id, event.target.value, 'title_de')
										}
									/>
								</Input.Group>
								<Input.Group style={{ display: 'flex' }}>
									<InputNumber
										min={1}
										max={100}
										step={1}
										placeholder="Value"
										style={{ marginRight: 6, flex: 1 }}
										defaultValue={part.value}
										addonAfter="%"
										value={part.value}
										onChange={(value) => onChangeArrayItem('part', part.id, value, 'value')}
									/>
									<InputNumber
										min={1}
										max={100000}
										step={1}
										placeholder="Order"
										style={{ marginRight: 6, width: '100px' }}
										defaultValue={part.order}
										value={part.order}
										onChange={(value) => onChangeArrayItem('part', part.id, value, 'order')}
									/>
									<Button onClick={() => onRemoveArrayItem('part', part.id)}>remove</Button>
								</Input.Group>
							</S.Part>
						))}
					<Button onClick={() => onAddArrayItem('part')} type="dashed" style={{ width: '100%' }}>
						<PlusOutlined />
						add part
					</Button>
				</Form.Item>
				<Form.Item label={`Cover (${complexCover ? 1 : 0}/1)`}>
					{isCoverLoading && <S.Loader />}
					<input
						type="file"
						ref={coverInputRef}
						name="complexCover"
						onChange={onUploadCover}
						style={{ display: 'none' }}
					/>
					<Button
						type="dashed"
						icon={<FileImageOutlined />}
						onClick={onUploadCoverClick}
						style={{ width: '100%' }}
						disabled={!!complexCover}>
						Upload cover
					</Button>
					<S.Images>
						{!!complexCover && (
							<S.Image>
								<S.Remove onClick={onRemoveCover}>
									<CloseOutlined
										style={{
											fontSize: '8px',
											color: '#000',
											width: '8px',
											display: 'block',
										}}
									/>
								</S.Remove>
								<Image
									src={complexCover.url}
									alt={complexCover.fileName}
									width={98}
									height={98}
									style={{ objectFit: 'cover' }}
								/>
							</S.Image>
						)}
					</S.Images>
				</Form.Item>
				<Form.Item label={`Photos (${complexPhotos.length}/20)`}>
					{isPhotosLoading && <S.Loader />}
					<input
						type="file"
						ref={photosInputRef}
						name="complexPhotos"
						onChange={onUploadPhotos}
						style={{ display: 'none' }}
						multiple
						maxLength={20}
					/>
					<Button
						type="dashed"
						icon={<FileImageOutlined />}
						onClick={onUploadPhotosClick}
						style={{ width: '100%' }}
						disabled={complexPhotos.length > 19}>
						Upload photos
					</Button>
					<S.Images>
						{complexPhotos.map((photo: IComplexPhoto) => (
							<S.Image key={photo.id}>
								<S.Remove onClick={() => onRemovePhoto(photo.id)}>
									<CloseOutlined
										style={{
											fontSize: '8px',
											color: '#000',
											width: '8px',
											display: 'block',
										}}
									/>
								</S.Remove>
								<Image
									src={photo.url}
									alt={photo.fileName}
									width={98}
									height={98}
									style={{ objectFit: 'cover' }}
								/>
							</S.Image>
						))}
					</S.Images>
				</Form.Item>
				<Form.Item name="consultations" label="Consultations">
					<Select
						mode="multiple"
						style={{ width: '100%' }}
						placeholder="Please select"
						options={consultations.map((item: IConsultation) => ({
							key: item.id,
							value: `userId: ${item.userId}`,
						}))}
						disabled
					/>
				</Form.Item>
				<Form.Item name="favourites" label="Favourites">
					<Select
						mode="multiple"
						style={{ width: '100%' }}
						placeholder="Please select"
						options={favourites.map((item: IFavourite) => ({
							key: item.id,
							value: `userId: ${item.userId}`,
						}))}
						disabled
					/>
				</Form.Item>
			</Form>
		</Drawer>
	);
};

export default EditComplexModal;
